import {
  Breadcrumb,
  InlineAlert,
  Page,
  PageContent,
  PageHeader,
  PageTitleBlock,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@planuze/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { DetailPageState } from '../components/detail-page-state.js';
import { AuditTab } from '../components/users/tabs/audit-tab.js';
import { DevicesTab } from '../components/users/tabs/devices-tab.js';
import { LicensesTab } from '../components/users/tabs/licenses-tab.js';
import { OrgsTab } from '../components/users/tabs/orgs-tab.js';
import { OverviewTab } from '../components/users/tabs/overview-tab.js';
import { SessionsTab } from '../components/users/tabs/sessions-tab.js';
import { UserDetailHeader } from '../components/users/user-detail-header.js';
import { useUserDetailContent } from '../content/sections/user-detail.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { useUserDetail } from '../hooks/use-users.js';

const routeApi = getRouteApi('/authenticated/users/$id');

export function UserDetailPage() {
  const { id } = routeApi.useParams();
  const navigate = useNavigate();
  const session = useStaffSession();
  const role = session?.role ?? 'readonly';
  const { data: user, isLoading, isError } = useUserDetail(id);
  const copy = useUserDetailContent();

  const goToList = () => {
    navigate({ to: '/users' }).catch(() => undefined);
  };
  const crumbName = user?.email ?? user?.name ?? copy.userFallbackLabel;
  const renderHeader = (label: string) => (
    <PageHeader>
      <PageTitleBlock>
        <Breadcrumb
          ariaLabel={copy.breadcrumbAriaLabel}
          items={[{ label: copy.usersBreadcrumb, onClick: goToList }, { label }]}
        />
      </PageTitleBlock>
    </PageHeader>
  );

  if (isError) {
    return (
      <DetailPageState
        header={renderHeader(copy.userFallbackLabel)}
        kind="error"
        message={copy.loadError}
      />
    );
  }
  if (isLoading || user === undefined) {
    return (
      <DetailPageState header={renderHeader(copy.userFallbackLabel)} kind="loading" message="" />
    );
  }
  return (
    <Page>
      {renderHeader(crumbName)}
      <PageContent className="space-y-6">
        <UserDetailHeader user={user} staffRole={role} />
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">{copy.tabs.overview}</TabsTrigger>
            <TabsTrigger value="orgs">{copy.tabs.orgs}</TabsTrigger>
            <TabsTrigger value="devices">{copy.tabs.devices}</TabsTrigger>
            <TabsTrigger value="licenses">{copy.tabs.licenses}</TabsTrigger>
            <TabsTrigger value="sessions">{copy.tabs.sessions}</TabsTrigger>
            <TabsTrigger value="audit">{copy.tabs.audit}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <OverviewTab user={user} />
          </TabsContent>
          <TabsContent value="orgs">
            <OrgsTab orgs={user.orgs} />
          </TabsContent>
          <TabsContent value="devices">
            {user.licensingAvailability.devices === 'available' ? (
              <DevicesTab devices={user.devices} userId={user.id} staffRole={role} />
            ) : (
              <InlineAlert variant="warning">{copy.dimensionUnavailable.devices}</InlineAlert>
            )}
          </TabsContent>
          <TabsContent value="licenses">
            {user.licensingAvailability.licenses === 'available' ? (
              <LicensesTab licenses={user.licenses} userId={user.id} staffRole={role} />
            ) : (
              <InlineAlert variant="warning">{copy.dimensionUnavailable.licenses}</InlineAlert>
            )}
          </TabsContent>
          <TabsContent value="sessions">
            {user.licensingAvailability.sessions === 'available' ? (
              <SessionsTab sessions={user.sessions} userId={user.id} staffRole={role} />
            ) : (
              <InlineAlert variant="warning">{copy.dimensionUnavailable.sessions}</InlineAlert>
            )}
          </TabsContent>
          <TabsContent value="audit">
            <AuditTab audit={user.recentAudit} userId={user.id} />
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  );
}
